import {
  SEARCH_MARKETING_SCHOOLS,
  PREVIEW_MARKETING_AUDIENCE,
  GET_MARKETING_MANAGEMENT,
  GET_CONTINUATION_PREVIEW,
  GET_OUTREACH_METRICS,
  GET_OUTREACH_CONTACT_HISTORY,
  GET_PUBLISHED_WORKFLOW_METRICS,
  GET_MARKETING_MIGRATION_PREVIEW,
  GET_MARKETING_ENROLLMENTS,
  GET_MARKETING_DELIVERIES,
  GET_MARKETING_OPERATIONAL_METRICS,
  SEARCH_MARKETING_USERS,
  SEARCH_MARKETING_CONTACTS,
  GET_OUTREACH_ENRICHMENT_BATCHES,
  GET_OUTREACH_ENRICHMENT_BATCH,
  GET_OUTREACH_ENRICHMENT_ATTEMPTS,
  GET_OUTREACH_ENRICHMENT_ATTEMPT_CONTACTS,
  GET_GIAS_REFRESH_STATUS,
  GET_OUTREACH_ENRICHMENT_SELECTION_SUMMARY,
  GET_OUTREACH_CONTACTS,
} from "./Queries";
import {
  ENROLL_AUDIENCE,
  ENROLL_SCHOOL_ONBOARDING,
  CREATE_WORKFLOW,
  MANAGE_VERSION,
  CONTINUE_PARTICIPANTS,
  STOP_OUTREACH_CONTACT,
  UPDATE_WORKFLOW,
  CREATE_DRAFT,
  DELETE_DRAFT,
  SAVE_DRAFT,
  VALIDATE_DRAFT,
  PUBLISH_DRAFT,
  PREVIEW_TEMPLATE,
  TEST_TEMPLATE,
  ENROLL_USER,
  ENROLL_CONTACTS,
  SAVE_SENDER,
  DELETE_SENDER,
  TEST_SENDER,
  UPDATE_SENDER_SIGNATURE_IMAGE,
  STOP_WORKFLOW_VERSION,
  RESTART_WORKFLOW_VERSION,
  MIGRATE_PARTICIPANTS,
  SET_WORKFLOW_SENDERS,
  SET_SENDER_ALLOCATIONS,
  STOP_ENROLLMENT,
  RETRY_DELIVERY,
  SET_MARKETING_AUTOMATION,
  LAUNCH_OUTREACH_ENRICHMENT,
  CANCEL_OUTREACH_ENRICHMENT,
  RETRY_OUTREACH_ENRICHMENT_ATTEMPT,
  UPDATE_OUTREACH_CONTACT,
  REFRESH_GIAS_SCHOOL_DATA,
  VALIDATE_OUTREACH_CONTACTS,
} from "./Mutations";

export class MarketingClient {
  constructor(client) {
    this.client = client;
  }
  async getOutreachMetrics() {
    const { data } = await this.client.query({
      query: GET_OUTREACH_METRICS,
      fetchPolicy: "network-only",
    });
    return data.outreachMetrics;
  }
  async getOutreachContactHistory(contactId, offset = 0) {
    const { data } = await this.client.query({
      query: GET_OUTREACH_CONTACT_HISTORY,
      variables: { contactId, offset },
      fetchPolicy: "network-only",
    });
    return data.outreachContactHistory;
  }
  stopOutreachContact = (contactId, reason) =>
    this.mutate(
      STOP_OUTREACH_CONTACT,
      { contactId, reason },
      "stopOutreachContact",
    );
  async getManagement() {
    const { data } = await this.client.query({
      query: GET_MARKETING_MANAGEMENT,
      fetchPolicy: "network-only",
    });
    return {
      ...data,
      // GraphQL choice enums are uppercase; workflow metadata uses model codes.
      marketingWorkflows: (data.marketingWorkflows || []).map((workflow) => ({
        ...workflow,
        workflowType: workflow.workflowType?.toLowerCase(),
      })),
    };
  }
  async searchEligibleSchools(variables) {
    const { data } = await this.client.query({
      query: SEARCH_MARKETING_SCHOOLS,
      variables,
      fetchPolicy: "network-only",
    });
    return data.marketingEligibleSchools;
  }
  async previewAudience(variables) {
    const { data } = await this.client.query({
      query: PREVIEW_MARKETING_AUDIENCE,
      variables,
      fetchPolicy: "network-only",
    });
    return data.marketingAudiencePreview;
  }
  enrollAudience = (variables) =>
    this.mutate(ENROLL_AUDIENCE, variables, "enrollMarketingAudience");
  enrollSchoolOnboarding = (schoolId) =>
    this.mutate(
      ENROLL_SCHOOL_ONBOARDING,
      { schoolId },
      "enrollSchoolOnboarding",
    );
  async searchEligibleUsers(variables) {
    const { data } = await this.client.query({
      query: SEARCH_MARKETING_USERS,
      variables,
      fetchPolicy: "network-only",
    });
    return data.marketingEligibleUsers;
  }
  async searchEligibleContacts(variables) {
    const { data } = await this.client.query({
      query: SEARCH_MARKETING_CONTACTS,
      variables,
      fetchPolicy: "network-only",
    });
    return data.marketingEligibleContacts;
  }
  async getPublishedWorkflowMetrics(workflowId, versionId = null) {
    const { data } = await this.client.query({
      query: GET_PUBLISHED_WORKFLOW_METRICS,
      variables: { workflowId, versionId },
      fetchPolicy: "network-only",
    });
    return data.marketingPublishedWorkflowMetrics;
  }
  async getMigrationPreview(sourceVersionId, destinationWorkflowId) {
    const { data } = await this.client.query({
      query: GET_MARKETING_MIGRATION_PREVIEW,
      variables: { sourceVersionId, destinationWorkflowId },
      fetchPolicy: "network-only",
    });
    return data.marketingMigrationPreview;
  }
  async getEnrollments(
    workflowId,
    status = "active",
    email = "",
    first = 10,
    after = null,
    versionId = null,
  ) {
    const { data } = await this.client.query({
      query: GET_MARKETING_ENROLLMENTS,
      variables: { workflowId, status, email, first, after, versionId },
      fetchPolicy: "network-only",
    });
    return data.marketingEnrollments;
  }
  async getDeliveries(
    workflowId,
    status = "",
    email = "",
    diagnosticCode = "",
    first = 10,
    after = null,
    versionId = null,
  ) {
    const { data } = await this.client.query({
      query: GET_MARKETING_DELIVERIES,
      variables: {
        workflowId,
        status,
        email,
        diagnosticCode,
        first,
        after,
        versionId,
      },
      fetchPolicy: "network-only",
    });
    return data.marketingDeliveries;
  }
  async getOperationalMetrics(workflowId = null, versionId = null) {
    const { data } = await this.client.query({
      query: GET_MARKETING_OPERATIONAL_METRICS,
      variables: { workflowId, versionId },
      fetchPolicy: "network-only",
    });
    return data.marketingOperationalMetrics;
  }
  async getContinuationPreview(
    sourceVersionId,
    destinationVersionId,
    offset = 0,
  ) {
    const { data } = await this.client.query({
      query: GET_CONTINUATION_PREVIEW,
      variables: { sourceVersionId, destinationVersionId, offset },
      fetchPolicy: "network-only",
    });
    return data.marketingContinuationPreview;
  }
  continueParticipants = (sourceVersionId, destinationVersionId, afterId = 0) =>
    this.mutate(
      CONTINUE_PARTICIPANTS,
      { sourceVersionId, destinationVersionId, afterId },
      "continueMarketingParticipants",
    );
  manageVersion = (versionId, action) =>
    this.mutate(
      MANAGE_VERSION,
      { versionId, action },
      "manageMarketingVersion",
    );
  async mutate(mutation, variables, key) {
    const { data } = await this.client.mutate({ mutation, variables });
    return data[key];
  }
  createWorkflow = (settings) =>
    this.mutate(CREATE_WORKFLOW, { settings }, "createMarketingWorkflow");
  updateWorkflow = (workflowId, settings) =>
    this.mutate(
      UPDATE_WORKFLOW,
      { workflowId, settings },
      "updateMarketingWorkflow",
    );
  createDraft = (workflowId, sourceVersionId = null) =>
    this.mutate(
      CREATE_DRAFT,
      { workflowId, sourceVersionId },
      "createMarketingDraft",
    );
  deleteDraft = (versionId) =>
    this.mutate(DELETE_DRAFT, { versionId }, "deleteMarketingDraft");
  saveDraft = (variables) =>
    this.mutate(SAVE_DRAFT, variables, "saveMarketingDraft");
  validateDraft = (versionId) =>
    this.mutate(VALIDATE_DRAFT, { versionId }, "validateMarketingDraft");
  publishDraft = (versionId) =>
    this.mutate(PUBLISH_DRAFT, { versionId }, "publishMarketingDraft");
  stopVersion = (versionId, allowNoDefault = false) =>
    this.mutate(
      STOP_WORKFLOW_VERSION,
      { versionId, allowNoDefault },
      "stopMarketingWorkflowVersion",
    );
  restartVersion = (versionId) =>
    this.mutate(
      RESTART_WORKFLOW_VERSION,
      { versionId },
      "restartMarketingWorkflowVersion",
    );
  migrateParticipants = (sourceVersionId, destinationWorkflowId) =>
    this.mutate(
      MIGRATE_PARTICIPANTS,
      { sourceVersionId, destinationWorkflowId },
      "migrateMarketingParticipants",
    );
  setWorkflowSenders = (workflowId, senderIds) =>
    this.mutate(
      SET_WORKFLOW_SENDERS,
      { workflowId, senderIds },
      "setMarketingWorkflowSenders",
    );
  setSenderAllocations = (senderId, allocations) =>
    this.mutate(
      SET_SENDER_ALLOCATIONS,
      { senderId, allocations },
      "setMarketingSenderAllocations",
    );
  previewTemplate = (
    templateId,
    userId = null,
    useMock = !userId,
    senderId = null,
    contactId = null,
  ) =>
    this.mutate(
      PREVIEW_TEMPLATE,
      { templateId, userId, useMock, senderId, contactId },
      "previewMarketingTemplate",
    );
  testTemplate = (
    templateId,
    userId,
    recipient,
    useMock = !userId,
    senderId = null,
    contactId = null,
  ) =>
    this.mutate(
      TEST_TEMPLATE,
      { templateId, userId, recipient, useMock, senderId, contactId },
      "testSendMarketingTemplate",
    );
  enrollUser = (userId, workflowId, versionId = null) =>
    this.mutate(
      ENROLL_USER,
      { userId, workflowId, versionId },
      "enrollMarketingUser",
    );
  enrollContacts = (contactIds, workflowId, versionId = null) =>
    this.mutate(
      ENROLL_CONTACTS,
      { contactIds, workflowId, versionId },
      "enrollMarketingContacts",
    );
  stopEnrollment = (enrollmentId, reason = "") =>
    this.mutate(
      STOP_ENROLLMENT,
      { enrollmentId, reason },
      "stopMarketingEnrollment",
    );
  retryDelivery = (deliveryId) =>
    this.mutate(RETRY_DELIVERY, { deliveryId }, "retryMarketingDelivery");
  setAutomation = (enabled, reason = "") =>
    this.mutate(
      SET_MARKETING_AUTOMATION,
      { enabled, reason },
      "setMarketingAutomation",
    );
  saveSender = (senderId, data, allocations = null) =>
    this.mutate(
      SAVE_SENDER,
      { senderId, data, allocations },
      "saveMarketingSender",
    );
  deleteSender = (senderId) =>
    this.mutate(DELETE_SENDER, { senderId }, "deleteMarketingSender");
  testSender = (senderId, recipient) =>
    this.mutate(TEST_SENDER, { senderId, recipient }, "testMarketingSender");
  updateSenderSignatureImage = (senderId, image = null, remove = false) =>
    this.mutate(
      UPDATE_SENDER_SIGNATURE_IMAGE,
      { senderId, image, remove },
      "updateMarketingSenderSignatureImage",
    );
  getOutreachEnrichmentBatches = (first = 20, after = null) =>
    this.client
      .query({
        query: GET_OUTREACH_ENRICHMENT_BATCHES,
        variables: { first, after },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.outreachEnrichmentBatches);
  getOutreachEnrichmentBatch = (batchId, variables = {}) =>
    this.client
      .query({
        query: GET_OUTREACH_ENRICHMENT_BATCH,
        variables: {
          batchId,
          attemptFirst: 25,
          contactFirst: 25,
          ...variables,
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data);
  getOutreachEnrichmentAttempts = (
    batchId,
    { first = 25, after = null } = {},
  ) =>
    this.client
      .query({
        query: GET_OUTREACH_ENRICHMENT_ATTEMPTS,
        variables: { batchId, first, after },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.outreachEnrichmentAttempts);
  getOutreachEnrichmentAttemptContacts = (
    attemptId,
    { first = 25, after = null } = {},
  ) =>
    this.client
      .query({
        query: GET_OUTREACH_ENRICHMENT_ATTEMPT_CONTACTS,
        variables: { attemptId, first, after },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.outreachContacts);
  getOutreachEnrichmentSelectionSummary = (variables) =>
    this.client
      .query({
        query: GET_OUTREACH_ENRICHMENT_SELECTION_SUMMARY,
        variables,
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.outreachEnrichmentSelectionSummary);
  getOutreachContacts = (variables = {}) =>
    this.client
      .query({
        query: GET_OUTREACH_CONTACTS,
        variables: { first: 25, ...variables },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.outreachContacts);
  validateOutreachContacts = (contactIds) =>
    this.mutate(
      VALIDATE_OUTREACH_CONTACTS,
      { contactIds },
      "validateOutreachContacts",
    );
  launchOutreachEnrichment = (variables) =>
    this.mutate(
      LAUNCH_OUTREACH_ENRICHMENT,
      variables,
      "launchOutreachEnrichment",
    );
  cancelOutreachEnrichment = (batchId) =>
    this.mutate(
      CANCEL_OUTREACH_ENRICHMENT,
      { batchId },
      "cancelOutreachEnrichment",
    );
  retryOutreachEnrichmentAttempt = (attemptId) =>
    this.mutate(
      RETRY_OUTREACH_ENRICHMENT_ATTEMPT,
      { attemptId },
      "retryOutreachEnrichmentAttempt",
    );
  updateOutreachContact = (variables) =>
    this.mutate(UPDATE_OUTREACH_CONTACT, variables, "updateOutreachContact");

  getGiasRefreshStatus = () =>
    this.client
      .query({
        query: GET_GIAS_REFRESH_STATUS,
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.giasRefreshStatus);
  refreshGiasSchoolData = () =>
    this.mutate(REFRESH_GIAS_SCHOOL_DATA, {}, "refreshGiasSchoolData");
}
